import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './ean.reducer';

export const Ean = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const eanList = useAppSelector(state => state.ean.entities);
  const loading = useAppSelector(state => state.ean.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="ean-heading" data-cy="EanHeading">
        <Translate contentKey="crawlerBackendApp.ean.home.title">Eans</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.ean.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/ean/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.ean.home.createLabel">Create new Ean</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {eanList && eanList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.ean.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('eanCode')}>
                  <Translate contentKey="crawlerBackendApp.ean.eanCode">Ean Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('eanCode')} />
                </th>
                <th className="hand" onClick={sort('switchingStatus')}>
                  <Translate contentKey="crawlerBackendApp.ean.switchingStatus">Switching Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('switchingStatus')} />
                </th>
                <th className="hand" onClick={sort('isResidential')}>
                  <Translate contentKey="crawlerBackendApp.ean.isResidential">Is Residential</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('isResidential')} />
                </th>
                <th className="hand" onClick={sort('isComplex')}>
                  <Translate contentKey="crawlerBackendApp.ean.isComplex">Is Complex</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('isComplex')} />
                </th>
                <th className="hand" onClick={sort('isSmartMeter')}>
                  <Translate contentKey="crawlerBackendApp.ean.isSmartMeter">Is Smart Meter</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('isSmartMeter')} />
                </th>
                <th className="hand" onClick={sort('gridArea')}>
                  <Translate contentKey="crawlerBackendApp.ean.gridArea">Grid Area</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('gridArea')} />
                </th>
                <th className="hand" onClick={sort('bagId')}>
                  <Translate contentKey="crawlerBackendApp.ean.bagId">Bag Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bagId')} />
                </th>
                <th className="hand" onClick={sort('bagBuildingId')}>
                  <Translate contentKey="crawlerBackendApp.ean.bagBuildingId">Bag Building Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bagBuildingId')} />
                </th>
                <th className="hand" onClick={sort('activeMeterNumber')}>
                  <Translate contentKey="crawlerBackendApp.ean.activeMeterNumber">Active Meter Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('activeMeterNumber')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.ean.gridOperator">Grid Operator</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {eanList.map((ean, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/ean/${ean.id}`} color="link" size="sm">
                      {ean.id}
                    </Button>
                  </td>
                  <td>{ean.eanCode}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.SwitchingStatus.${ean.switchingStatus}`} />
                  </td>
                  <td>{ean.isResidential ? 'true' : 'false'}</td>
                  <td>{ean.isComplex ? 'true' : 'false'}</td>
                  <td>{ean.isSmartMeter ? 'true' : 'false'}</td>
                  <td>{ean.gridArea}</td>
                  <td>{ean.bagId}</td>
                  <td>{ean.bagBuildingId}</td>
                  <td>{ean.activeMeterNumber}</td>
                  <td>
                    {ean.gridOperator ? (
                      <Link to={`/grid-operator/${ean.gridOperator.id}`}>{ean.gridOperator.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/ean/${ean.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/ean/${ean.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/ean/${ean.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.ean.home.notFound">No Eans found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Ean;
